// Radha — Disputes, on the direct path. Second module built from the template.
//
// Same shape as CollectionsLive: Supabase Auth session, then live RLS-scoped
// rows from PostgREST. Two panels — the dispute case list and the customer
// health/churn panel — both direct, no gateway. Kept plain on purpose.
import React, { useMemo } from "react";
import { fmtMoney } from "../lib/money";

import { useDisputes, useCustomerHealth } from "../hooks/useDisputesDirect";
import { ModuleProposals } from "../components/ModuleProposals";
import { ModuleChart } from "../components/ModuleChart";
import { useTableSearch, SearchBar, ExportButton } from "../components/TableSearch";
import { slaState, slaSummary } from "../lib/disputeSla";
import { fmtMoney as inr } from "../lib/money";


const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "10px 8px" };
const numTd: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };
const statusLabel = (s: string) => s.replace(/_/g, " ");
const statusTd: React.CSSProperties = { ...td, textTransform: "capitalize" };
const pill = (c: string): React.CSSProperties => ({
  marginLeft: 6, padding: "1px 6px", fontSize: 11, color: c,
  border: `1px solid ${c}`, borderRadius: 6,
});
const BAND: Record<string, string> = {
  healthy: "var(--status-good)", watch: "var(--status-warning)", at_risk: "var(--status-critical)", critical: "var(--status-critical)",
};

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>{label}</div>
      <div style={{ font: "600 20px Inter" }}>{value}</div>
    </div>
  );
}

// Panel only — the shell owns the header, tabs and sign-out.
export function DisputesPanel() {
  const disputes = useDisputes();
  const health = useCustomerHealth();

  const now = Date.now();
  const totals = useMemo(() => {
    const list = disputes.data ?? [];
    const sla = slaSummary(list, now);
    return {
      count: list.length,
      disputed: list.reduce((s, d) => s + Number(d.disputed_amount), 0),
      highValue: list.filter((d) => d.is_high_value).length,
      open: list.filter((d) => d.status !== "resolved" && d.status !== "withdrawn").length,
      slaBreaching: sla.breaching,
      slaCompliance: sla.compliancePct,
    };
  }, [disputes.data, now]);

  const { q, setQ, filtered } = useTableSearch(disputes.data,
    (d) => `${d.customer_name} ${d.invoice_number} ${d.category} ${d.status}`);

  if (disputes.isLoading) return <p style={{ padding: 24 }}>Loading disputes…</p>;
  if (disputes.error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(disputes.error as Error).message}</p>;

  return (
    <div>
      <div style={{ display: "flex", gap: 24, margin: "4px 0 20px", flexWrap: "wrap" }}>
        <Stat label="Cases" value={String(totals.count)} />
        <Stat label="Open" value={String(totals.open)} />
        <Stat label="Disputed" value={inr(totals.disputed)} />
        <Stat label={`High-value (≥${fmtMoney(1_000_000)})`} value={String(totals.highValue)} />
        <div title="Open disputes past their resolution SLA (7d high-value, else 15d).">
          <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>SLA breaching</div>
          <div style={{ font: "600 20px Inter", color: totals.slaBreaching > 0 ? "var(--status-critical)" : "var(--status-good)" }}>{totals.slaBreaching}</div>
        </div>
        <div title="Share of resolved disputes that were closed within SLA.">
          <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>SLA compliance</div>
          <div style={{ font: "600 20px Inter" }}>{totals.slaCompliance == null ? "—" : `${totals.slaCompliance}%`}</div>
        </div>
      </div>

      <ModuleChart title="Disputed amount by category" money color="var(--status-critical)"
        data={[...(disputes.data ?? []).reduce((m, d) => m.set(d.category, (m.get(d.category) ?? 0) + Number(d.disputed_amount)), new Map<string, number>())]
          .map(([label, value]) => ({ label, value }))} />

      <ModuleProposals table="dispute_proposals" />

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <SearchBar q={q} setQ={setQ} count={filtered.length} total={(disputes.data ?? []).length} placeholder="Search customer / invoice / category…" />
        <ExportButton name="Disputes" rows={() => filtered.map((d) => ({
          Customer: d.customer_name, Invoice: d.invoice_number, Disputed: Number(d.disputed_amount),
          "High value": d.is_high_value ? "yes" : "no", Category: d.category, Status: d.status, Verdict: d.verdict ?? "",
          "SLA target (d)": slaState(d, now).target, SLA: slaState(d, now).label,
        }))} />
      </div>

      <div className="sanctuary-scroll-x">
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
            <th style={th}>Customer</th><th style={th}>Invoice</th><th style={{ ...th, textAlign: "right" }}>Disputed</th>
            <th style={th}>Category</th><th style={th}>Status</th><th style={th}>SLA</th><th style={th}>Verdict</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((d) => (
            <tr key={d.id} style={{ borderTop: "1px solid var(--border)" }}>
              <td style={td}>
                {d.customer_name}
                {d.is_high_value && <span style={pill("var(--status-critical)")}>≥{fmtMoney(1_000_000)}</span>}
              </td>
              <td style={td}>{d.invoice_number}</td>
              <td style={numTd}>{inr(d.disputed_amount)}</td>
              <td style={statusTd}>{statusLabel(d.category)}</td>
              <td style={statusTd}>{statusLabel(d.status)}</td>
              <td style={td}>
                {(() => { const s = slaState(d, now); return (
                  <span style={{ fontSize: 11.5, color: s.color, border: `1px solid ${s.color}`, borderRadius: 6, padding: "1px 6px" }}>{s.label}</span>
                ); })()}
              </td>
              <td style={{ ...td, color: "var(--fg-muted)" }}>{d.verdict ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <h3 style={{ font: "600 15px Inter", margin: "28px 0 8px" }}>Customer health</h3>
      {health.isLoading ? (
        <p style={{ color: "var(--fg-muted)" }}>loading…</p>
      ) : (
        <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
              <th style={th}>Customer</th><th style={{ ...th, textAlign: "right" }}>Health</th><th style={{ ...th, textAlign: "right" }}>Churn</th>
              <th style={{ ...th, textAlign: "right" }}>Open disputes</th><th style={th}>Band</th>
            </tr>
          </thead>
          <tbody>
            {(health.data ?? []).map((h) => (
              <tr key={h.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={td}>{h.customer_name}</td>
                <td style={numTd}>{Math.round(Number(h.health_score))}</td>
                <td style={numTd}>{Math.round(Number(h.churn_probability) * 100)}%</td>
                <td style={numTd}>{h.open_disputes}</td>
                <td style={{ ...statusTd, color: BAND[h.health_band] ?? "var(--fg)" }}>{statusLabel(h.health_band)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

